import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useServiceCenterSettings } from '../../../hooks/useServiceCenterSettings';

interface HeroProps {
  onBookClick: () => void;
}

export default function Hero({ onBookClick }: HeroProps) {
  const { t } = useTranslation();
  const { latestOrderTime, earliestReturnTime } = useServiceCenterSettings();
  const [navHeight, setNavHeight] = useState(80);

  useEffect(() => {
    const updateNavHeight = () => {
      const nav = document.querySelector('nav');
      if (nav) {
        setNavHeight(nav.getBoundingClientRect().height);
      }
    };
    updateNavHeight();
    window.addEventListener('resize', updateNavHeight);
    return () => window.removeEventListener('resize', updateNavHeight);
  }, []);

  return (
    <section
      id="home"
      className="relative w-full min-h-screen"
      style={{
        backgroundImage: 'url(https://static.readdy.ai/image/bd37f9b321d8d487bb2df5b36d703d09/f2d3fbab8c18b45cb1399f65832a3916.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'top center',
      }}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40 pointer-events-none"></div>

      <div
        className="relative z-10 w-full"
        style={{ paddingTop: `calc(${navHeight}px + 20px)`, paddingBottom: '60px' }}
      >
        <div className="max-w-7xl mx-auto px-3 xs:px-4 md:px-6 pt-4 xs:pt-6 md:pt-16">
          <div className="grid md:grid-cols-5 gap-4 sm:gap-6 md:gap-12 items-start md:items-center">

            {/* Linke Seite: Logo + Headlines */}
            <div className="md:col-span-3">
              <div className="mb-2 xs:mb-3 md:mb-4">
                {/* Logo + Klosters + Ski-Service Kurier */}
                <div className="flex items-center gap-2 xs:gap-3">
                  <img
                    src="https://static.readdy.ai/image/bd37f9b321d8d487bb2df5b36d703d09/5bc315f20d8b71200f0abd862b82a739.png"
                    alt="Ski Service Kurier Logo"
                    width="112"
                    height="112"
                    fetchPriority="high"
                    loading="eager"
                    className="hidden md:block h-16 xs:h-20 md:h-28 w-auto object-contain drop-shadow-lg flex-shrink-0"
                  />
                  <div className="flex flex-col justify-center leading-none min-w-0">
                    <img
                      src="https://public.readdy.ai/ai/img_res/37a9eaec-a4dc-46dd-a3bb-52522c41453b.png"
                      alt="Klosters"
                      width="200"
                      height="56"
                      fetchPriority="high"
                      loading="eager"
                      className="h-7 xs:h-8 md:h-14 w-auto object-contain drop-shadow-lg"
                    />
                    <p
                      className="text-white text-[9px] xs:text-[10px] md:text-xs font-bold tracking-[0.2em] xs:tracking-[0.25em] uppercase mt-0.5 xs:mt-1 drop-shadow whitespace-nowrap"
                      style={{ textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}
                    >
                      {t('hero.subtitle')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Andrist Sport Partnerschaft – präsent, eigenständig */}
              <div className="flex items-center gap-2 xs:gap-3 md:gap-4 mb-2 xs:mb-3 md:mb-4">
                <span
                  className="text-white/60 text-[8px] xs:text-[9px] md:text-[10px] font-semibold uppercase tracking-[0.18em] whitespace-nowrap"
                >
                  {t('hero.inCollabWith')}
                </span>
                <div className="w-px h-4 xs:h-5 md:h-6 bg-white/30 flex-shrink-0"></div>
                <img
                  src="https://public.readdy.ai/ai/img_res/4a11d33e-f711-4fe6-b1a5-b44acfa67ee7.png"
                  alt="Andrist Sport"
                  className="h-5 xs:h-6 md:h-10 w-auto object-contain drop-shadow-md flex-shrink-0"
                  style={{ filter: 'brightness(0) invert(1)', opacity: 0.9 }}
                />
              </div>

              <div className="inline-block bg-[#2B8FBF] text-white px-3 xs:px-4 py-1 xs:py-1.5 rounded-full text-[11px] xs:text-xs md:text-sm font-bold mb-2 xs:mb-3 md:mb-5 whitespace-nowrap">
                {t('hero.badge', { orderDeadline: latestOrderTime, returnTime: earliestReturnTime })}
              </div>

              <h1 className="hidden md:block text-white mb-3 xs:mb-4 md:mb-6 leading-tight">
                <div 
                  className="font-black mb-0.5 leading-tight" 
                  style={{ 
                    fontSize: 'clamp(1.25rem, 5vw, 3rem)',
                    textShadow: '2px 2px 4px rgba(0,0,0,0.3)' 
                  }}
                >
                  {t('hero.headline1')}
                </div>
                <div 
                  className="font-black mb-0.5 leading-tight" 
                  style={{ 
                    fontSize: 'clamp(1.25rem, 5.5vw, 3.75rem)',
                    textShadow: '2px 2px 4px rgba(0,0,0,0.3)' 
                  }}
                >
                  {t('hero.headline2')}
                </div>
                <div 
                  className="font-black leading-tight mt-0.5" 
                  style={{ 
                    fontSize: 'clamp(1rem, 4vw, 2.25rem)',
                    textShadow: '2px 2px 4px rgba(0,0,0,0.3)' 
                  }}
                >
                  {t('hero.headline3', { returnTime: earliestReturnTime })}
                </div>
              </h1>

            </div>

            {/* Rechte Seite: Weiße Karte */}
            <div className="md:col-span-2 bg-white/95 backdrop-blur-sm rounded-2xl xs:rounded-3xl p-4 xs:p-5 md:p-8 w-full box-border">
              <h2 
                className="font-black text-gray-900 mb-1 xs:mb-1.5 leading-tight"
                style={{ fontSize: 'clamp(1rem, 4vw, 1.875rem)' }}
              >
                {t('hero.cardTitle')}
              </h2>
              <p 
                className="text-gray-600 mb-1 xs:mb-1.5 font-semibold tracking-wide"
                style={{ fontSize: 'clamp(0.75rem, 2.5vw, 1rem)' }}
              >
                {t('hero.cardSubtitle')}
              </p>
              <p 
                className="text-gray-700 leading-snug mb-3 xs:mb-4 md:mb-6"
                style={{ fontSize: 'clamp(0.7rem, 2vw, 0.875rem)' }}
              >
                {t('hero.cardDescription', { returnTime: earliestReturnTime })}
              </p>

              <button
                onClick={onBookClick}
                className="w-full bg-[#2B8FBF] text-white px-6 xs:px-8 py-2.5 xs:py-3 md:py-4 rounded-full font-bold hover:bg-[#2480ad] transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                style={{ fontSize: 'clamp(0.813rem, 2.5vw, 1rem)' }}
              >
                {t('hero.ctaButton')}
                <i className="ri-arrow-right-line"></i>
              </button>

              <div className="mt-2.5 xs:mt-3 md:mt-4 flex flex-wrap items-center gap-1 xs:gap-1.5 md:gap-2">
                <div className="flex items-center gap-1 text-gray-500 text-[10px] xs:text-xs font-semibold uppercase tracking-wider whitespace-nowrap">
                  <i className="ri-map-pin-2-fill text-gray-400"></i>
                  <span className="hidden xs:inline">{t('hero.availableIn')}</span>
                  <span className="xs:hidden">Orte:</span>
                </div>
                {['7247 Saas', '7249 Serneus', '7250 Klosters', '7252 Klosters-Dorf'].map((ort) => (
                  <span
                    key={ort}
                    className="bg-[#2B8FBF]/10 border border-[#2B8FBF] text-[#1a6a8f] text-[10px] xs:text-xs font-semibold px-1.5 xs:px-2.5 py-0.5 rounded-full whitespace-nowrap"
                  >
                    {ort}
                  </span>
                ))}
              </div>

            </div>

          </div>
        </div>
      </div>
    </section>
  );
}
